import React from "react";

import { Badge, Headline, Small } from "@fronterahealth/frontera-ui-components";

import { LearnerEhrProgrammingQuery } from "@api/graphql/types-and-hooks";
import { EhrChildCollectionSection } from "@pages/ClientEhrProgramming/EhrChildCollectionSection";
import { EhrSessionDataSection } from "@pages/ClientEhrProgramming/EhrSessionDataSection";
import { EhrStoredFieldList } from "@pages/ClientEhrProgramming/EhrStoredFieldList";

export type EhrProgrammingGoal = NonNullable<LearnerEhrProgrammingQuery["learnerEhrProgramming"]>["goals"][number];
export type EhrProgrammingTarget = EhrProgrammingGoal["targets"][number];

interface EhrProgrammingItemDetailProps {
  learnerId: string;
  goal: EhrProgrammingGoal;
  // Set when a target is selected; the goal is then its parent.
  target?: EhrProgrammingTarget;
}

const goalName = (goal: EhrProgrammingGoal) => goal.name || "Untitled goal";
const targetName = (target: EhrProgrammingTarget) => target.name || "Untitled target";

// The selected goal or target: its stored fields, phase/event history and session data.
export const EhrProgrammingItemDetail: React.FC<EhrProgrammingItemDetailProps> = ({ learnerId, goal, target }) => {
  const item = target ?? goal;
  const dates = [
    item.createdDate && `Created ${item.createdDate}`,
    item.lastUpdatedDate && `Last updated ${item.lastUpdatedDate}`,
  ].filter(Boolean);

  return (
    <div className="flex flex-col gap-5 p-5 w-full bg-white rounded-lg border border-limestone-200">
      <div className="flex flex-col gap-1">
        {target ? (
          <Headline
            as="h2"
            size="extra-small"
            className="flex min-w-0 items-center gap-2 ph-no-capture"
            title={`${goalName(goal)} > ${targetName(target)}`}
          >
            <span className="min-w-0 truncate">{goalName(goal)}</span>
            <span className="shrink-0 text-text-secondary">&gt;</span>
            <span className="min-w-0 truncate">{targetName(target)}</span>
          </Headline>
        ) : (
          <Headline as="h2" size="extra-small" className="ph-no-capture">
            {goalName(goal)}
          </Headline>
        )}
        {dates.length > 0 && <Small className="text-text-secondary">{dates.join(" · ")}</Small>}
        {item.externalId && (
          <div className="flex items-center gap-2 pt-1">
            <Small className="text-text-secondary">External ID</Small>
            <Badge appearance="neutral" text={item.externalId} />
          </div>
        )}
      </div>
      <EhrStoredFieldList fields={item.fields} />
      {item.collections.map((collection) => (
        <EhrChildCollectionSection key={collection.key} collection={collection} />
      ))}
      <EhrSessionDataSection
        learnerId={learnerId}
        scope={target ? { targetId: target.id } : { goalId: goal.id }}
        summary={item.sessionSummary}
        label={target ? "Session data" : "Goal-level session data"}
      />
    </div>
  );
};
